import { Action, configureStore, ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import { DEV } from 'application/consts';

import { createAcceptanceAdvancedListener } from './slices/acceptanceAdvancedSlice/acceptanceAdvancedListener';
import { createGlobalPollingListener } from './slices/globalPollingSlice/globalPollingListener';

import rootReducer from './slices';

export interface ThunkExtra {
  apolloClient: ApolloClient<NormalizedCacheObject>;
}

const persistConfig = {
  key: 'root',
  storage: storageSession,
  whitelist: ['account'],
  version: 1,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const createStore = (apolloClient: ApolloClient<NormalizedCacheObject>) => {
  const extra: ThunkExtra = { apolloClient };

  const acceptanceAdvancedListener = createAcceptanceAdvancedListener(apolloClient);
  const globalPollingListener = createGlobalPollingListener(apolloClient);

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER,
            'persist/PERSIST',
            'acceptanceAdvancedSigningProcess/startAcceptanceAdvancedSigning',
            'globalPolling/startGlobalPolling',
            'account/fetchData/pending',
            'account/fetchData/fulfilled',
            'account/fetchData/rejected',
          ],
        },
        thunk: {
          extraArgument: extra,
        },
      }).prepend([globalPollingListener.middleware, acceptanceAdvancedListener.middleware]),
    devTools: DEV,
  });

  const persistor = persistStore(store);

  return { store, persistor };
};

export type AppStore = ReturnType<typeof createStore>['store'];
export type AppPersistor = ReturnType<typeof createStore>['persistor'];
export type AppDispatch = ThunkDispatch<RootState, ThunkExtra, UnknownAction>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  { apolloClient: ApolloClient<NormalizedCacheObject> },
  Action<string>
>;

export * from './slices';
